import React from 'react';
import { Plus, Trash, GraduationCap, BookOpen, FileText, Hash, Clock } from '@phosphor-icons/react';
import EnhancedRichTextEditor from '../../EnhancedRichTextEditor';

export default function EssayModule({ metadata, onChange }) {
  const handleChange = (field, value) => {
    onChange({ ...metadata, [field]: value });
  };

  const addReference = () => {
    handleChange('references', [...(metadata.references || []), '']);
  };

  const updateReference = (index, value) => {
    const newRefs = [...(metadata.references || [])];
    newRefs[index] = value;
    handleChange('references', newRefs);
  };

  const removeReference = (index) => {
    const newRefs = (metadata.references || []).filter((_, i) => i !== index);
    handleChange('references', newRefs);
  };

  const addFootnote = () => {
    handleChange('footnotes', [...(metadata.footnotes || []), { number: '', text: '' }]);
  };

  const updateFootnote = (index, field, value) => {
    const newFootnotes = [...(metadata.footnotes || [])];
    newFootnotes[index] = { ...newFootnotes[index], [field]: value };
    handleChange('footnotes', newFootnotes);
  };

  const removeFootnote = (index) => {
    const newFootnotes = (metadata.footnotes || []).filter((_, i) => i !== index);
    handleChange('footnotes', newFootnotes);
  };

  const addBibliography = () => {
    handleChange('bibliography', [...(metadata.bibliography || []), '']);
  };

  const updateBibliography = (index, value) => {
    const newBib = [...(metadata.bibliography || [])];
    newBib[index] = value;
    handleChange('bibliography', newBib);
  };

  const removeBibliography = (index) => {
    const newBib = (metadata.bibliography || []).filter((_, i) => i !== index);
    handleChange('bibliography', newBib);
  };

  return (
    <div className="space-y-6">
      {/* Abstract */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <FileText size={16} />
          Abstract / Summary
        </label>
        <textarea
          value={metadata.abstract || ''}
          onChange={(e) => handleChange('abstract', e.target.value)}
          placeholder="Brief abstract summarizing the essay (150-250 words)..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none"
          rows={4}
          maxLength={500}
        />
        <p className="text-xs text-muted-foreground mt-1">
          {(metadata.abstract || '').length}/500 characters
        </p>
      </div>

      {/* Essay Content */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <GraduationCap size={16} />
          Essay Content <span className="text-red-500">*</span>
        </label>
        <EnhancedRichTextEditor
          value={metadata.essay_content || ''}
          onChange={(html) => handleChange('essay_content', html)}
          placeholder="Write your essay here..."
        />
      </div>

      {/* Keywords */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Hash size={16} />
          Keywords
        </label>
        <input
          type="text"
          value={(metadata.keywords || []).join(', ')}
          onChange={(e) => {
            const keywords = e.target.value.split(',').map(kw => kw.trim()).filter(Boolean);
            handleChange('keywords', keywords);
          }}
          placeholder="Academic keywords separated by commas..."
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        />
        <p className="text-xs text-muted-foreground mt-1">
          5-10 keywords for academic indexing
        </p>
      </div>

      {/* Reading Time */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Clock size={16} />
          Estimated Reading Time (minutes)
        </label>
        <input
          type="number"
          value={metadata.reading_time || ''}
          onChange={(e) => handleChange('reading_time', parseInt(e.target.value) || 0)}
          placeholder="e.g., 10"
          min="1"
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Leave empty to auto-calculate based on word count
        </p>
      </div>

      {/* Footnotes */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium flex items-center gap-2">
            <FileText size={16} />
            Footnotes
          </label>
          <button
            onClick={addFootnote}
            className="flex items-center gap-1 px-3 py-1 text-sm rounded-lg bg-muted hover:bg-muted/80"
          >
            <Plus size={14} weight="bold" />
            Add Footnote
          </button>
        </div>

        {(metadata.footnotes || []).length === 0 && (
          <p className="text-sm text-muted-foreground italic">No footnotes added</p>
        )}

        <div className="space-y-3">
          {(metadata.footnotes || []).map((footnote, index) => (
            <div key={index} className="p-4 bg-muted/50 rounded-lg group relative">
              <button
                onClick={() => removeFootnote(index)}
                className="absolute top-2 right-2 p-1 rounded text-red-500 opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all"
              >
                <Trash size={14} />
              </button>
              <div className="grid grid-cols-4 gap-3">
                <input
                  type="text"
                  value={footnote.number || ''}
                  onChange={(e) => updateFootnote(index, 'number', e.target.value)}
                  placeholder="#"
                  className="px-3 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors text-sm"
                />
                <textarea
                  value={footnote.text || ''}
                  onChange={(e) => updateFootnote(index, 'text', e.target.value)}
                  placeholder="Footnote text..."
                  className="col-span-3 px-3 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors text-sm resize-none"
                  rows={2}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* References */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium flex items-center gap-2">
            <BookOpen size={16} />
            References & Citations
          </label>
          <button
            onClick={addReference}
            className="flex items-center gap-1 px-3 py-1 text-sm rounded-lg bg-muted hover:bg-muted/80"
          >
            <Plus size={14} weight="bold" />
            Add Reference
          </button>
        </div>

        {(metadata.references || []).length === 0 && (
          <p className="text-sm text-muted-foreground italic">No references added</p>
        )}

        <div className="space-y-2">
          {(metadata.references || []).map((ref, index) => (
            <div key={index} className="flex gap-2 items-center group">
              <span className="text-xs text-muted-foreground font-mono">[{index + 1}]</span>
              <input
                type="text"
                value={ref}
                onChange={(e) => updateReference(index, e.target.value)}
                placeholder="Reference in APA, MLA, or Chicago format"
                className="flex-1 px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors text-sm"
              />
              <button
                onClick={() => removeReference(index)}
                className="p-2 rounded-lg text-red-500 opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all"
              >
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Bibliography */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium flex items-center gap-2">
            <BookOpen size={16} />
            Bibliography
          </label>
          <button
            onClick={addBibliography}
            className="flex items-center gap-1 px-3 py-1 text-sm rounded-lg bg-muted hover:bg-muted/80"
          >
            <Plus size={14} weight="bold" />
            Add Entry
          </button>
        </div>

        {(metadata.bibliography || []).length === 0 && (
          <p className="text-sm text-muted-foreground italic">No bibliography entries</p>
        )}

        <div className="space-y-2">
          {(metadata.bibliography || []).map((entry, index) => (
            <div key={index} className="flex gap-2 items-center group">
              <input
                type="text"
                value={entry}
                onChange={(e) => updateBibliography(index, e.target.value)}
                placeholder="Bibliography entry (book, journal, website, etc.)"
                className="flex-1 px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors text-sm"
              />
              <button
                onClick={() => removeBibliography(index)}
                className="p-2 rounded-lg text-red-500 opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all"
              >
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Publication Info */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Publication Information (Optional)
        </label>
        <input
          type="text"
          value={metadata.publication_info || ''}
          onChange={(e) => handleChange('publication_info', e.target.value)}
          placeholder="e.g., Originally published in Journal XYZ, Vol. 12, 2024"
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        />
      </div>
    </div>
  );
}
